"use client";

import { useEffect, useRef, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import { useIsomorphicLayoutEffect } from "framer-motion";
import { UsersRound, Landmark, GraduationCap, SlidersHorizontal } from "lucide-react";

import type { PageHomeJourneyStep } from "@/sanity/pages/PageHomeTemplate";
import JourneyMediaFrame, { Artwork, type JourneyTimeline } from "./JourneyMediaFrame";
import { journeyCopy } from "./journeyMediaCopy";
import styles from "./journeyElite.module.scss";

const CITIES = [
  { lat: 50.11, lon: 8.68 },
  { lat: 40.71, lon: -74.01 },
  { lat: 51.50, lon: -0.12 },
  { lat: 25.20, lon: 55.27 },
];
const CONNECTIONS = [[1, 2], [2, 0], [0, 3]];
const ICONS = [UsersRound, Landmark, GraduationCap, SlidersHorizontal];
const LABELS = {
  en: ["Exclusive inner circle", "Institutional capital", "Coaching & masterclasses", "Flexible trading"],
  de: ["Exklusiver Kreis", "Institutionelles Kapital", "Coaching & Masterclasses", "Flexibles Trading"],
  br: ["Círculo exclusivo", "Capital institucional", "Coaching e masterclasses", "Trading flexível"],
};
const TAU = Math.PI * 2;
const radians = (angle: number) => angle * Math.PI / 180;
const clamp = (value: number) => Math.max(0, Math.min(1, value));
type Point = { x: number; y: number; z: number; land?: boolean };

function globePoint(lat: number, lon: number): Point {
  return { x: Math.cos(radians(lat)) * Math.sin(radians(lon)), y: Math.sin(radians(lat)), z: Math.cos(radians(lat)) * Math.cos(radians(lon)) };
}

const GRID = [
  ...[-60, -30, 0, 30, 60].map((latitude) => Array.from({ length: 73 }, (_, n) => globePoint(latitude, n * 5 - 180))),
  ...[-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150].map((longitude) => Array.from({ length: 37 }, (_, n) => globePoint(n * 5 - 90, longitude))),
];

function EliteGlobe({ index, timeline, cities }: { index: number; timeline: JourneyTimeline; cities: string[] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pinRefs = useRef<(HTMLDivElement | null)[]>([]);
  const drawRef = useRef<(progress: number) => void>(() => {});
  const indexRef = useRef(index);
  const [ready, setReady] = useState(false);

  useIsomorphicLayoutEffect(() => {
    indexRef.current = index;
    drawRef.current(timeline.progress.get());
  }, [index, timeline.progress]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    let disposed = false;
    let points: Point[] = [];
    let resolution = 1;
    const image = new Image();
    const locations = CITIES.map(({ lat, lon }) => globePoint(lat, lon));
    const draw = (progress: number) => {
      if (!points.length || disposed) return;
      const stage = indexRef.current;
      const phase = timeline.reducedMotion ? 0.04 : (stage + progress) / 4;
      const previousCity = CITIES[(stage + CITIES.length - 1) % CITIES.length];
      const currentCity = CITIES[stage];
      const turn = clamp(progress / 0.55);
      const easedTurn = turn * turn * (3 - 2 * turn);
      const longitude = timeline.reducedMotion ? -CITIES[0].lon : -previousCity.lon + (previousCity.lon - currentCity.lon) * easedTurn;
      const rotation = radians(longitude);
      const cos = Math.cos(rotation), sin = Math.sin(rotation);
      const tilt = radians(28), ct = Math.cos(tilt), st = Math.sin(tilt);
      const project = (point: Point, altitude = 1) => {
        const x = point.x * cos + point.z * sin;
        const z = point.z * cos - point.x * sin;
        return { x: 310 + x * 228 * altitude, y: 234 - (point.y * ct - z * st) * 228 * altitude, z: point.y * st + z * ct };
      };
      context.setTransform(resolution, 0, 0, resolution, 0, 0);
      context.clearRect(0, 0, 620, 460);
      const glow = context.createRadialGradient(310, 185, 30, 310, 234, 230);
      glow.addColorStop(0, "#0a1f16"); glow.addColorStop(0.75, "#040c08"); glow.addColorStop(1, "#010302");
      context.fillStyle = glow;
      context.beginPath(); context.arc(310, 234, 228, 0, TAU); context.fill();
      context.strokeStyle = "#dbffe53d"; context.lineWidth = 0.75; context.stroke();
      context.strokeStyle = "#dbffe510"; context.lineWidth = 0.5;
      const gridLine = (points: Point[]) => {
        let previous = project(points[0]);
        context.beginPath();
        for (const point of points.slice(1)) {
          const p = project(point);
          if (p.z > 0.03 && previous.z > 0.03) { context.moveTo(previous.x, previous.y); context.lineTo(p.x, p.y); }
          previous = p;
        }
        context.stroke();
      };
      GRID.forEach(gridLine);
      // Batch the surface dots by depth, keeping the original land mask and a bounded draw cost.
      const paths = Array.from({ length: 6 }, () => new Path2D());
      for (const point of points) {
        const p = project(point);
        if (p.z <= 0.025) continue;
        const depth = Math.min(2, Math.floor(p.z * 3));
        const radius = point.land ? 0.9 : 0.36;
        const path = paths[depth + (point.land ? 3 : 0)];
        path.moveTo(p.x + radius, p.y); path.arc(p.x, p.y, radius, 0, TAU);
      }
      paths.forEach((path, i) => {
        context.fillStyle = i < 3 ? `rgba(219,255,229,${0.10 + i * 0.035})` : `rgba(219,255,229,${0.28 + (i - 3) * 0.24})`;
        context.fill(path);
      });
      CONNECTIONS.forEach(([from, to], routeIndex) => {
        const a = locations[from], b = locations[to];
        const dot = a.x * b.x + a.y * b.y + a.z * b.z;
        const distance = Math.acos(Math.max(-1, Math.min(1, dot)));
        const along = (t: number) => {
          const divisor = Math.sin(distance) || 1;
          const wa = Math.sin((1 - t) * distance) / divisor, wb = Math.sin(t * distance) / divisor;
          return project({ x: a.x * wa + b.x * wb, y: a.y * wa + b.y * wb, z: a.z * wa + b.z * wb }, 1 + Math.sin(t * Math.PI) * 0.13);
        };
        const pulse = (phase * 5 + routeIndex * 0.32) % 1;
        let previous = along(0);
        for (let n = 1; n <= 52; n++) {
          const t = n / 52, p = along(t);
          if (p.z > 0.02 && previous.z > 0.02) {
            const light = timeline.reducedMotion ? 0 : Math.max(0, 1 - Math.abs(pulse - t) / 0.18);
            context.strokeStyle = `rgba(143,237,184,${0.14 + light * 0.80})`;
            context.lineWidth = 0.8 + light * 0.8;
            context.beginPath(); context.moveTo(previous.x, previous.y); context.lineTo(p.x, p.y); context.stroke();
          }
          previous = p;
        }
        if (!timeline.reducedMotion) {
          const head = along(pulse);
          if (head.z > 0.02) {
            context.shadowColor = "#8fedb8"; context.shadowBlur = 10;
            context.fillStyle = "#dbffe5";
            context.beginPath(); context.arc(head.x, head.y, 2.2, 0, TAU); context.fill(); context.shadowBlur = 0;
          }
        }
      });
      locations.forEach((location, i) => {
        const p = project(location), pin = pinRefs.current[i];
        if (!pin) return;
        pin.style.left = `${p.x / 6.2}%`; pin.style.top = `${p.y / 4.6}%`;
        pin.style.opacity = `${clamp((p.z - 0.12) * 4)}`;
        pin.dataset.side = p.x > 340 ? "left" : "right";
        pin.dataset.active = `${i === indexRef.current}`;
        pin.dataset.label = `${i === indexRef.current && p.z > 0.45}`;
        if (p.z > 0.12) {
          const pulse = timeline.reducedMotion ? 0.35 : (phase * 7 + i * 0.23) % 1;
          context.strokeStyle = `rgba(143,237,184,${(1 - pulse) * 0.48})`;
          context.lineWidth = 0.9;
          context.beginPath(); context.arc(p.x, p.y, 15 + pulse * 23, 0, TAU); context.stroke();
        }
      });
    };
    drawRef.current = draw;
    const measure = () => {
      resolution = Math.min(window.devicePixelRatio || 1, 1.5) * canvas.clientWidth / 620;
      canvas.width = Math.round(620 * resolution); canvas.height = Math.round(460 * resolution);
      draw(timeline.progress.get());
    };
    image.onload = () => {
      if (disposed) return;
      const mask = document.createElement("canvas");
      mask.width = 512; mask.height = 256;
      const maskContext = mask.getContext("2d", { willReadFrequently: true });
      if (!maskContext) return;
      maskContext.drawImage(image, 0, 0, 512, 256);
      const pixels = maskContext.getImageData(0, 0, 512, 256).data;
      for (let lat = -88; lat <= 88; lat += 2) {
        const count = Math.round(180 * Math.cos(radians(lat)));
        for (let n = 0; n < count; n++) {
          const lon = n / count * 360 - 180;
          const x = Math.min(511, Math.floor((lon + 180) / 360 * 512));
          const y = Math.min(255, Math.floor((90 - lat) / 180 * 256));
          points.push({ ...globePoint(lat, lon), land: pixels[(y * 512 + x) * 4] > 110 });
        }
      }
      measure(); setReady(true);
    };
    image.src = "/elite/globe-land.png";
    const resize = new ResizeObserver(measure); resize.observe(canvas);
    const unsubscribe = timeline.progress.on("change", draw);
    measure();
    return () => { disposed = true; image.onload = null; resize.disconnect(); unsubscribe(); drawRef.current = () => {}; points = []; };
  }, [timeline.progress, timeline.reducedMotion]);

  return (
    <div className={styles.globeLayer} data-ready={ready}>
      <Artwork name="globe-0.webp" className={styles.fallback} />
      <canvas ref={canvasRef} className={styles.globe} />
      {CITIES.map((_, i) => (
        <div key={i} ref={(element) => { pinRefs.current[i] = element; }} className={styles.pin}>
          <div className={styles.pinMark}><Artwork name="logo-mint.svg" /></div>
          <span className={styles.city}>{cities[i]}</span>
        </div>
      ))}
    </div>
  );
}

export default function JourneyEliteMedia({ step, language, className }: { step: PageHomeJourneyStep; language?: string; className?: string }) {
  const copy = journeyCopy(language);
  const key = stegaClean(language);
  const defaults = LABELS[key === "de" || key === "br" ? key : "en"];
  const labels = defaults.map((label, i) => stegaClean(step.mediaEliteBenefits?.[i]) || label);
  const cities = CITIES.map((_, i) => stegaClean(step.mediaCities?.[i]) || copy.cities[i]);
  return (
    <JourneyMediaFrame variant="elite" language={language} length={4} interval={4800} className={className}>
      {(index, timeline) => {
        const Icon = ICONS[index];
        return <>
          <EliteGlobe index={index} timeline={timeline} cities={cities} />
          <div className={styles.community}>
            <div className={styles.eyebrow}><Artwork name="logo-mint.svg" /><span>{stegaClean(step.mediaCardTitle) || "IQ Elite"}</span><span className={styles.connectionDot} /></div>
            <div key={index} className={styles.benefit}><span className={styles.benefitIcon}><Icon /></span><span>{labels[index]}</span></div>
            <div className={styles.progress}>{labels.map((label, i) => <span key={label} data-active={i === index} />)}</div>
          </div>
        </>;
      }}
    </JourneyMediaFrame>
  );
}
